import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Client } from 'src/app/models/client.model';
import { ClientServicesService } from 'src/app/services/admin/client-services.service';
import { DialogService } from 'src/app/services/dialog.service';
import { AthenaService } from 'src/app/services/emr/athena.service';
import { AthenaDepartment } from 'src/app/services/models/athena.model';
import { SnackService } from 'src/app/services/snack.service';
import { Departament } from './client-department.model';

@Component({
  selector: 'app-client-department-list',
  templateUrl: './client-department-list.component.html',
  styleUrls: ['./client-department-list.component.scss'],
})
export class ClientDepartmentListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() client: Client;
  showInput = false;
  nameDepartment = new UntypedFormControl('');
  athenaDepartmentControl = new UntypedFormControl('');
  departments: Departament[] = [];
  athenaDepartments: AthenaDepartment[] = [];
  isSearchingAthena = false;
  selectedAthenaDepartment: AthenaDepartment | null = null;
  editingDepartment: Departament | null = null;

  private destroy$ = new Subject<void>();

  get clientId(): string {
    return this.client?.client_id;
  }

  get isAthenaClient(): boolean {
    return this.client?.emr_data?.emr_name?.toLowerCase().includes('athena') ?? false;
  }

  get displayedColumns(): string[] {
    return this.isAthenaClient ? ['name', 'athena_department', 'actions'] : ['name', 'actions'];
  }

  constructor(
    public dialogService: DialogService,
    public clientServicesService: ClientServicesService,
    private athenaService: AthenaService,
    private snackBarService: SnackService
  ) {}

  ngOnInit(): void {
    this.getDepartments();
    this.setupAthenaTypeahead();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // ngOnChanges runs before ngOnInit, so without the firstChange guard both hooks fire
    // getDepartments() and the first render costs two identical requests.
    if (changes.client?.firstChange === false && this.clientId) {
      this.resetForm();
      this.getDepartments();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDepartments(): void {
    if (!this.clientId) {
      return;
    }
    this.clientServicesService
      .getClientDepartments(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.departments = data;
      });
  }

  setupAthenaTypeahead(): void {
    this.athenaDepartmentControl.valueChanges
      .pipe(
        filter((value) => typeof value === 'string' && value.length > 1),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.isSearchingAthena = true;
          this.selectedAthenaDepartment = null;
        }),
        switchMap((value: string) =>
          this.athenaService.searchDepartments(value, this.clientId).pipe(
            catchError(() => {
              this.isSearchingAthena = false;
              return of({ totalcount: 0, departments: [] });
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.athenaDepartments = res.departments;
        this.isSearchingAthena = false;
      });
  }

  onAthenaDepartmentSelected(department: AthenaDepartment): void {
    this.selectedAthenaDepartment = department;
    this.nameDepartment.setValue(department.name);
  }

  displayAthenaDepartment(department: AthenaDepartment): string {
    return department ? department.name : '';
  }

  editDepartment(department: Departament): void {
    this.editingDepartment = department;
    this.showInput = true;
    this.nameDepartment.setValue(department.name);
    if (department.athena_department_id) {
      const athenaDept: AthenaDepartment = {
        departmentid: Number(department.athena_department_id),
        name: department.athena_department_name || '',
      };
      this.athenaDepartmentControl.setValue(athenaDept);
      this.selectedAthenaDepartment = athenaDept;
    } else {
      this.athenaDepartmentControl.setValue('');
      this.selectedAthenaDepartment = null;
    }
  }

  cancelEdit(): void {
    this.resetForm();
  }

  deleteDepartment(id: string): void {
    this.clientServicesService.deleteClientDepartment(id).subscribe({
      next: () => {
        this.snackBarService.genericSnackBar('Department has been deleted.', ['success-snackbar'], 2000);
        this.resetForm();
        this.getDepartments();
      },
      error: () => {
        this.snackBarService.genericSnackBar('Error deleting department.', ['error-snackbar'], 2000);
      },
    });
  }

  saveDepartment(): void {
    if (this.editingDepartment) {
      this.updateExistingDepartment();
    } else {
      this.createNewDepartment();
    }
  }

  private createNewDepartment(): void {
    const data: Departament = {
      name: this.nameDepartment.value,
      active: true,
      client_id: this.clientId,
    };

    if (this.selectedAthenaDepartment) {
      data.athena_department_id = String(this.selectedAthenaDepartment.departmentid);
      data.athena_department_name = this.selectedAthenaDepartment.name;
    }

    this.clientServicesService.saveClientDepartment(data).subscribe({
      next: () => {
        this.snackBarService.genericSnackBar('Department has been created.', ['success-snackbar'], 2000);
        this.resetForm();
        this.getDepartments();
      },
      error: () => {
        this.snackBarService.genericSnackBar('Error creating department.', ['error-snackbar'], 2000);
      },
    });
  }

  private updateExistingDepartment(): void {
    const data: Partial<Departament> = {
      name: this.nameDepartment.value,
    };

    if (this.selectedAthenaDepartment) {
      data.athena_department_id = String(this.selectedAthenaDepartment.departmentid);
      data.athena_department_name = this.selectedAthenaDepartment.name;
    } else {
      data.athena_department_id = '';
      data.athena_department_name = '';
    }

    this.clientServicesService.updateClientDepartment(this.editingDepartment.id, data).subscribe({
      next: () => {
        this.snackBarService.genericSnackBar('Department has been updated.', ['success-snackbar'], 2000);
        this.resetForm();
        this.getDepartments();
      },
      error: () => {
        this.snackBarService.genericSnackBar('Error updating department.', ['error-snackbar'], 2000);
      },
    });
  }

  private resetForm(): void {
    this.nameDepartment.reset();
    this.athenaDepartmentControl.reset();
    this.selectedAthenaDepartment = null;
    this.editingDepartment = null;
    this.athenaDepartments = [];
    this.showInput = false;
  }
}
